import React from "react";
import {
  Card,
  Row,
  Col,
  Tag,
  Typography,
  Space,
  List,
  Statistic,
  Progress,
  Alert,
  Empty,
  Tooltip,
  Badge,
  Timeline,
} from "antd";
import {
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  ShoppingOutlined,
  DollarOutlined,
  BankOutlined,
  UserOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  TeamOutlined,
  HomeOutlined,
} from "@ant-design/icons";

const { Title, Text, Paragraph } = Typography;

/**
 * Composant pour afficher les détails des dépendances d'un utilisateur
 * Utilisé dans les modals de confirmation et les rapports
 */
const DependencyList = ({
  dependencies,
  detailed,
  user,
  summary,
  showSummary = true,
}) => {
  /**
   * Obtient l'icône appropriée pour un type de dépendance
   */
  const getDependencyIcon = (type) => {
    const icons = {
      orders: <ShoppingOutlined />,
      expenses: <DollarOutlined />,
      financial: <BankOutlined />,
      assignments: <UserOutlined />,
      ecommerce: <ShoppingOutlined />,
      warehouse: <HomeOutlined />,
      documents: <FileTextOutlined />,
      teams: <TeamOutlined />,
    };
    return icons[type] || <InfoCircleOutlined />;
  };

  /**
   * Obtient la couleur pour un niveau de criticité
   */
  const getCriticalityColor = (critical) => {
    return critical ? "error" : "warning";
  };

  /**
   * Obtient la couleur de la bordure pour les cartes de dépendance
   */
  const getBorderColor = (critical) => {
    return critical ? "#ff4d4f" : "#faad14";
  };

  /**
   * Obtient la couleur de fond pour les cartes
   */
  const getBackgroundColor = (critical) => {
    return critical ? "#fff2f0" : "#fffbf0";
  };

  /**
   * Formate un montant en FCFA
   */
  const formatAmount = (amount) => {
    if (!amount) return "0";
    return new Intl.NumberFormat("fr-FR").format(amount) + " FCFA";
  };

  /**
   * Formate une date
   */
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("fr-FR");
  };

  /**
   * Rendu du résumé statistique
   */
  const renderSummary = () => {
    if (!showSummary || !summary) return null;

    return (
      <Card
        title="Résumé des dépendances"
        size="small"
        style={{ marginBottom: 16 }}
      >
        <Row gutter={16}>
          <Col span={6}>
            <Statistic
              title="Total"
              value={summary.totalDependencies || 0}
              prefix={<InfoCircleOutlined />}
              valueStyle={{
                color: summary.totalDependencies > 0 ? "#faad14" : "#52c41a",
              }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Critiques"
              value={summary.criticalDependencies || 0}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{
                color: summary.criticalDependencies > 0 ? "#ff4d4f" : "#52c41a",
              }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Attention"
              value={
                (summary.totalDependencies || 0) -
                (summary.criticalDependencies || 0)
              }
              prefix={<WarningOutlined />}
              valueStyle={{ color: "#faad14" }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Dernière activité"
              value={
                summary.lastActivity
                  ? Math.floor(
                      (Date.now() - new Date(summary.lastActivity)) /
                        (1000 * 60 * 60 * 24)
                    )
                  : "N/A"
              }
              suffix={summary.lastActivity ? "jours" : ""}
              prefix={<ClockCircleOutlined />}
            />
          </Col>
        </Row>
      </Card>
    );
  };

  /**
   * Rendu des détails d'une dépendance
   */
  const renderDependencyDetails = (dep) => {
    const details = dep.details || {};
    const tags = [];

    if (details.unpaidCount > 0) {
      tags.push(
        <Tag color="red" key="unpaid">
          {details.unpaidCount} non payée(s)
        </Tag>
      );
    }

    if (details.totalAmount) {
      tags.push(
        <Tag color="blue" key="amount">
          Total: {formatAmount(details.totalAmount)}
        </Tag>
      );
    }

    if (details.lastOrderDate) {
      tags.push(
        <Tag color="orange" key="date">
          Dernier: {formatDate(details.lastOrderDate)}
        </Tag>
      );
    }

    if (details.pendingCount > 0) {
      tags.push(
        <Tag color="volcano" key="pending">
          {details.pendingCount} en attente
        </Tag>
      );
    }

    if (details.averageAmount) {
      tags.push(
        <Tag color="cyan" key="average">
          Moyenne: {formatAmount(details.averageAmount)}
        </Tag>
      );
    }

    return tags;
  };

  /**
   * Rendu de la liste des dépendances principales
   */
  const renderDependencies = () => {
    if (!dependencies || dependencies.length === 0) {
      return (
        <Empty
          image={
            <CheckCircleOutlined style={{ fontSize: 48, color: "#52c41a" }} />
          }
          description="Aucune dépendance détectée"
        >
          <Text type="success">
            Cette entité peut être supprimée en toute sécurité.
          </Text>
        </Empty>
      );
    }

    return (
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        {dependencies.map((dep, index) => (
          <Badge.Ribbon
            key={index}
            text={dep.critical ? "Critique" : "Attention"}
            color={dep.critical ? "red" : "orange"}
          >
            <Card
              size="small"
              style={{
                borderLeft: `4px solid ${getBorderColor(dep.critical)}`,
                backgroundColor: getBackgroundColor(dep.critical),
              }}
            >
              <Row align="middle" gutter={16}>
                <Col flex="none">
                  <div
                    style={{
                      fontSize: 24,
                      color: getBorderColor(dep.critical),
                    }}
                  >
                    {getDependencyIcon(dep.type)}
                  </div>
                </Col>
                <Col flex="auto">
                  <Space
                    direction="vertical"
                    size={4}
                    style={{ width: "100%" }}
                  >
                    <Text strong>{dep.message}</Text>

                    {dep.description && (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {dep.description}
                      </Text>
                    )}

                    <Space size="small" wrap>
                      {renderDependencyDetails(dep)}
                    </Space>

                    {dep.impact && (
                      <Alert
                        message={dep.impact}
                        type={dep.critical ? "error" : "warning"}
                        showIcon={false}
                        size="small"
                        style={{ marginTop: 8 }}
                      />
                    )}
                  </Space>
                </Col>
              </Row>
            </Card>
          </Badge.Ribbon>
        ))}
      </Space>
    );
  };

  /**
   * Rendu des informations détaillées si disponibles
   */
  const renderDetailedInfo = () => {
    if (!detailed || Object.keys(detailed).length === 0) return null;

    const sections = [];

    // Section Commandes
    if (detailed.orders && detailed.orders.length > 0) {
      sections.push({
        title: "Commandes récentes",
        icon: <ShoppingOutlined />,
        data: detailed.orders.slice(0, 5),
        renderItem: (order) => (
          <Timeline.Item
            key={order.id}
            color={order.status === "pending" ? "red" : "green"}
          >
            <Space>
              <Text>#{order.id}</Text>
              <Tag color={order.type === "sale" ? "blue" : "purple"}>
                {order.type === "sale" ? "Vente" : "Achat"}
              </Tag>
              <Text strong>{formatAmount(order.total)}</Text>
              <Text type="secondary">{formatDate(order.date)}</Text>
            </Space>
          </Timeline.Item>
        ),
      });
    }

    // Section Financière
    if (detailed.financial && Object.keys(detailed.financial).length > 0) {
      const { financial } = detailed;
      sections.push({
        title: "Situation financière",
        icon: <BankOutlined />,
        custom: (
          <Row gutter={16}>
            {financial.balance && (
              <Col span={8}>
                <Statistic
                  title="Solde"
                  value={financial.balance}
                  precision={0}
                  prefix="FCFA"
                  valueStyle={{
                    color: financial.balance > 0 ? "#52c41a" : "#ff4d4f",
                  }}
                />
              </Col>
            )}
            {financial.totalSales && (
              <Col span={8}>
                <Statistic
                  title="Total ventes"
                  value={financial.totalSales}
                  precision={0}
                  prefix="FCFA"
                  valueStyle={{ color: "#173f5f" }}
                />
              </Col>
            )}
            {financial.totalPurchases && (
              <Col span={8}>
                <Statistic
                  title="Total achats"
                  value={financial.totalPurchases}
                  precision={0}
                  prefix="FCFA"
                  valueStyle={{ color: "#faad14" }}
                />
              </Col>
            )}
          </Row>
        ),
      });
    }

    if (sections.length === 0) return null;

    return (
      <Card
        title="Informations détaillées"
        size="small"
        style={{ marginTop: 16 }}
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          {sections.map((section, index) => (
            <div key={index}>
              <Title level={5}>
                <Space>
                  {section.icon}
                  {section.title}
                </Space>
              </Title>

              {section.custom ? (
                section.custom
              ) : (
                <Timeline size="small">
                  {section.data.map(section.renderItem)}
                </Timeline>
              )}
            </div>
          ))}
        </Space>
      </Card>
    );
  };

  /**
   * Rendu principal du composant
   */
  return (
    <div>
      {renderSummary()}
      {renderDependencies()}
      {renderDetailedInfo()}
    </div>
  );
};

export default DependencyList;
